// Cambio de marca desde la pantalla "Marca", para las anchos donde el shell nuevo no
// está — ADR-2026-08-04-cambiar-de-marca-desde-la-pantalla-de-marca.
//
// En desktop, la tarjeta de marca de la sidebar despliega el menú con las marcas de la
// cuenta (ADR-2026-08-03-tarjeta-marca-abre-menu-de-marcas). Esa sidebar existe recién
// desde 1024px: abajo de eso la pantalla de Marca se había quedado SIN ningún acceso
// propio —lo dice el comentario que dejó ADR-2026-07-31-mi-marca-card-marca-pantalla al
// sacar la card vieja—, y el único camino era el menú de la hamburguesa.
//
// No se reusa el `MenuMarcas` del shell: aquél se posiciona con coordenadas medidas
// contra el `<aside>` y crece al costado, dos cosas que en un teléfono no existen. Lo
// que sí se comparte es lo que importa que no se bifurque: el avatar de la marca
// (`AvatarMarca`), el orden nombre + rubro, y la puerta de plan de "Añadir nueva marca".

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { SheetLateral } from './SheetLateral';
import { AvatarMarca } from '../pages/estilos/EstiloMinimal';
import { getUserPlan, planAllowsMultiBrand } from '../lib/planUtils';
import type { BrandRow } from '../hooks/useBrandProfile';

export function SelectorMarcaMobile({
  marcas,
  marcaActivaId,
  cambiarMarca,
  cambiando,
  className = '',
  abierto: abiertoControlado,
  onAbiertoChange,
}: {
  marcas: BrandRow[];
  marcaActivaId: string | null;
  cambiarMarca: (id: string) => void;
  /** Mientras el cambio está en vuelo: la lista no acepta otro tap. */
  cambiando: boolean;
  className?: string;
  /**
   * Controlado y SIN la tarjeta disparadora: el sheet lo abre otro elemento. En Mi
   * marca lo abre el nombre de la cabecera de identidad, que en mobile pasó a ser el
   * disparador — ADR-2026-10-05-mi-marca-cabecera-en-mobile.
   */
  abierto?: boolean;
  onAbiertoChange?: (abierto: boolean) => void;
}) {
  const navigate = useNavigate();
  const [abiertoPropio, setAbiertoPropio] = useState(false);
  const controlado = abiertoControlado !== undefined;
  const abierto = controlado ? abiertoControlado : abiertoPropio;
  const setAbierto = (v: boolean) => {
    if (!controlado) setAbiertoPropio(v);
    onAbiertoChange?.(v);
  };
  // El plan se consulta al INTENTAR crear y no al abrir, igual que en el menú del
  // shell: casi todas las aperturas son para cambiar de marca.
  const [pidiendoPlan, setPidiendoPlan] = useState(false);

  // Sin marcas cargadas todavía no hay nada que mostrar; con el perfil resuelto
  // siempre hay al menos una.
  if (marcas.length === 0) return null;
  const activa = marcas.find((m) => m.id === marcaActivaId) ?? marcas[0];

  const elegir = (id: string) => {
    setAbierto(false);
    if (id !== marcaActivaId) cambiarMarca(id);
  };

  /**
   * Multi-marca es una función de plan pago. El criterio es el MISMO que el del menú
   * del shell y el del Sidebar de producción (`planAllowsMultiBrand`); lo que cambia es
   * a dónde se manda cuando no alcanza: acá se NAVEGA a la pantalla de planes, porque
   * el catálogo como overlay lo provee el shell de desktop, que en estos anchos no está.
   */
  const crearMarca = async () => {
    if (pidiendoPlan) return;
    setPidiendoPlan(true);
    try {
      const plan = await getUserPlan();
      setAbierto(false);
      navigate(
        planAllowsMultiBrand(plan)
          ? '/redes-sociales/descripcion-brand?mode=additional'
          : '/subscription',
      );
    } finally {
      setPidiendoPlan(false);
    }
  };

  return (
    <>
      {/* El disparador dice sobre QUÉ marca se está trabajando —los productos y los
          modelos de abajo son suyos— y de paso es el control para cambiarla. Es el
          mismo contenido que la tarjeta de la sidebar: avatar, nombre y rubro. */}
      {!controlado && (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        aria-haspopup="dialog"
        className={`flex w-full items-center gap-3 rounded-2xl border border-black/[0.07] bg-white px-3 py-2.5 text-left active:opacity-70 transition-opacity ${className}`}
      >
        <AvatarMarca
          logo={activa.logo_url}
          nombre={activa.name}
          className="h-9 w-9 shrink-0 rounded-lg"
          claseInicial="text-[13px]"
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[15px] font-semibold text-brand-black/90">
            {activa.name}
          </span>
          {activa.rubro && (
            <span className="truncate text-[12.5px] leading-[16px] text-black/45">
              {activa.rubro}
            </span>
          )}
        </span>
        {/* El mismo glifo que la tarjeta de la sidebar: dos flechas encontradas, que
            leen "cambiar" y no "abrir" ni "ir a". */}
        <span className="material-symbols-rounded shrink-0 text-[20px] text-black/40">
          unfold_more
        </span>
      </button>
      )}

      <SheetLateral
        open={abierto}
        onClose={() => setAbierto(false)}
        titulo="Cambiar de marca"
        // La lista son las marcas de la cuenta —normalmente pocas—, así que el sheet
        // mide lo que ocupan en vez de tomarse la pantalla
        // (ADR-2026-08-04-el-sheet-de-marcas-mide-lo-que-su-lista).
        altoPorContenido
      >
        <div className="flex flex-col gap-1">
          {marcas.map((m) => {
            const esActiva = m.id === marcaActivaId;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => elegir(m.id)}
                disabled={cambiando}
                className={`flex items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors disabled:opacity-50 ${
                  esActiva ? 'bg-brand-primary/[0.06]' : 'hover:bg-black/[0.03]'
                }`}
              >
                <AvatarMarca
                  logo={m.logo_url}
                  nombre={m.name}
                  className="h-9 w-9 shrink-0 rounded-lg"
                  claseInicial="text-[13px]"
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[15px] font-medium text-brand-black/90">
                    {m.name}
                  </span>
                  {m.rubro && (
                    <span className="truncate text-[12.5px] leading-[16px] text-black/45">
                      {m.rubro}
                    </span>
                  )}
                </span>
                {esActiva && (
                  <span className="material-symbols-rounded shrink-0 text-[20px] text-brand-primary">
                    check
                  </span>
                )}
              </button>
            );
          })}

          <div className="my-1.5 h-px bg-black/[0.07]" />

          <button
            type="button"
            onClick={crearMarca}
            disabled={pidiendoPlan}
            className="flex items-center gap-3 rounded-xl px-2.5 py-2.5 text-left text-[15px] font-medium text-brand-black/85 transition-colors hover:bg-black/[0.03] disabled:opacity-50"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-black/[0.04] text-brand-black/60">
              <Plus className="h-4.5 w-4.5" />
            </span>
            Añadir nueva marca
          </button>
        </div>
      </SheetLateral>
    </>
  );
}
